import { useState } from 'react';
import { ActionMenu, ActionList, TextInput } from '@primer/react';
import { PlusIcon, SearchIcon } from '@primer/octicons-react';
import styled from 'styled-components';
import { AddButton, ListToken } from './shared/tokens';
import { SectionContainer, ItemsContainer, ItemsList } from './shared/section-styles';
import { SectionTitleWithGuidance, SectionWarning } from './shared/section-guidance';
import { IPRAttributes } from '@upright/common/model/input/companies/products/ipr';
import {
  ATTRIBUTE_GUIDANCE,
  AttributeSection,
} from '@upright/common/model/input/companies/products/ipr/attribute-guidance';

interface SpecialAttribute {
  id: SpecialAttributeType;
  title: string;
  groupTitle: string;
  advanced: boolean;
}

export type SpecialAttributeType = keyof IPRAttributes;

/**
 * The special attribute flags fill two sections of the Advanced attributes tab: the business model
 * ones, which have their own rules and tooltip (PLT-7430), and the rest.
 */
export type SpecialAttributesSection = Extract<AttributeSection, 'businessModel' | 'specialAttributes'>;

const SECTION_TITLES: Record<SpecialAttributesSection, string> = {
  businessModel: 'Business model',
  specialAttributes: 'Special attributes',
};

interface SpecialAttributesProps {
  section: SpecialAttributesSection;
  attributes: SpecialAttributeType[];
  onAttributeChange: (attribute: SpecialAttributeType, value: boolean) => void;
  warning?: string;
}

const BUSINESS_MODEL_GROUP = 'Business model attributes';

const SPECIAL_ATTRIBUTES: SpecialAttribute[] = [
  // Business model attributes
  {
    id: 'IsPhysicalRetail',
    title: 'Physical Retail',
    groupTitle: BUSINESS_MODEL_GROUP,
    advanced: true,
  },
  {
    id: 'IsOnlineRetail',
    title: 'Online Retail',
    groupTitle: BUSINESS_MODEL_GROUP,
    advanced: true,
  },
  { id: 'IsTrading', title: 'Trading', groupTitle: BUSINESS_MODEL_GROUP, advanced: true },
  { id: 'IsInstallationOf', title: 'Installation of', groupTitle: BUSINESS_MODEL_GROUP, advanced: true },
  { id: 'IsAssemblyOf', title: 'Assembly of', groupTitle: BUSINESS_MODEL_GROUP, advanced: true },
  {
    id: 'IsRepairOrMaintenanceOf',
    title: 'Repair or maintenance of',
    groupTitle: BUSINESS_MODEL_GROUP,
    advanced: true,
  },
  { id: 'IsLicensingOf', title: 'Licensing of', groupTitle: BUSINESS_MODEL_GROUP, advanced: true },
  { id: 'IsDistributionOf', title: 'Distribution of', groupTitle: BUSINESS_MODEL_GROUP, advanced: true },
  { id: 'IsWholesale', title: 'Wholesale', groupTitle: BUSINESS_MODEL_GROUP, advanced: true },
  { id: 'IsRental', title: 'Rental', groupTitle: BUSINESS_MODEL_GROUP, advanced: true },
  { id: 'IsLeasing', title: 'Leasing', groupTitle: BUSINESS_MODEL_GROUP, advanced: true },
  {
    id: 'UltraLowCostCommerce',
    title: 'Ultra low cost commerce',
    groupTitle: BUSINESS_MODEL_GROUP,
    advanced: true,
  },
  // other attributes
  {
    id: 'FollowingJournalisticEthics',
    title: 'Follows journalistic ethics',
    groupTitle: 'Content and media attributes',
    advanced: true,
  },
  {
    id: 'InDevelopingCountries',
    title: 'In developing countries',
    groupTitle: 'Geographical attributes',
    advanced: true,
  },
  {
    id: 'WithoutAnimalTesting',
    title: 'Without animal testing',
    groupTitle: 'Product testing attributes',
    advanced: true,
  },
];

function sectionOf(attribute: SpecialAttribute): SpecialAttributesSection {
  return attribute.groupTitle === BUSINESS_MODEL_GROUP ? 'businessModel' : 'specialAttributes';
}

export function SpecialAttributes({ section, attributes, onAttributeChange, warning }: SpecialAttributesProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const sectionAttributes = SPECIAL_ATTRIBUTES.filter(attr => sectionOf(attr) === section);
  const shownAttributes = attributes.filter(id => sectionAttributes.some(attr => attr.id === id));

  const getAvailableAttributesByGroup = () => {
    const result: Record<string, SpecialAttribute[]> = {};

    // Group available attributes by their groupTitle, filtered by search term
    const availableAttributes = sectionAttributes.filter(
      attr =>
        !attributes.includes(attr.id) &&
        (attr.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          attr.groupTitle.toLowerCase().includes(searchTerm.toLowerCase())),
    );

    availableAttributes.forEach(attr => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!result[attr.groupTitle]) {
        result[attr.groupTitle] = [];
      }
      result[attr.groupTitle].push(attr);
    });

    return result;
  };

  const addAttribute = (attribute: SpecialAttributeType) => {
    onAttributeChange(attribute, true);
    setIsMenuOpen(false);
    setSearchTerm('');
  };

  const handleMenuOpenChange = (open: boolean) => {
    setIsMenuOpen(open);
    if (!open) {
      setSearchTerm('');
    }
  };

  const removeAttribute = (attribute: SpecialAttributeType) => {
    onAttributeChange(attribute, false);
  };

  const renderItem = (attribute: SpecialAttribute) => (
    <ActionList.Item
      key={attribute.id}
      onSelect={() => {
        addAttribute(attribute.id);
      }}
    >
      {attribute.title}
    </ActionList.Item>
  );

  return (
    <SectionContainer>
      <SectionTitleWithGuidance title={SECTION_TITLES[section]} guidance={ATTRIBUTE_GUIDANCE[section]} />

      <ItemsContainer>
        <ItemsList>
          {shownAttributes.map(attributeId => {
            const attribute = sectionAttributes.find(attr => attr.id === attributeId);
            return (
              <ListToken
                key={attributeId}
                size="medium"
                onRemove={() => {
                  removeAttribute(attributeId);
                }}
                text={attribute?.title || attributeId}
              />
            );
          })}
          <ActionMenu open={isMenuOpen} onOpenChange={handleMenuOpenChange}>
            <ActionMenu.Anchor>
              <AddButton size="medium" leadingVisual={PlusIcon} text="Add attribute" />
            </ActionMenu.Anchor>

            <ActionMenu.Overlay width="medium">
              <SearchContainer>
                <TextInput
                  placeholder="Search attributes..."
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value);
                  }}
                  leadingVisual={SearchIcon}
                  size="small"
                />
              </SearchContainer>
              <ScrollableActionListContainer>
                <ActionList>
                  {(() => {
                    const availableByGroup = Object.entries(getAvailableAttributesByGroup());

                    if (availableByGroup.length === 0) {
                      return (
                        <ActionList.Group title="Select an attribute...">
                          <ActionList.Item disabled>
                            {searchTerm ? 'No matching attributes found' : 'All attributes added'}
                          </ActionList.Item>
                        </ActionList.Group>
                      );
                    }
                    // A single group (always so for the business model section) needs no submenu.
                    if (availableByGroup.length === 1) {
                      return (
                        <ActionList.Group title="Select an attribute...">
                          {availableByGroup[0][1].map(renderItem)}
                        </ActionList.Group>
                      );
                    }
                    return availableByGroup.map(([groupName, groupAttributes]) => (
                      <ActionMenu key={groupName}>
                        <ActionMenu.Anchor>
                          <ActionList.Item>{groupName}</ActionList.Item>
                        </ActionMenu.Anchor>
                        <ActionMenu.Overlay width="medium">
                          <ScrollableActionListContainer>
                            <ActionList>{groupAttributes.map(renderItem)}</ActionList>
                          </ScrollableActionListContainer>
                        </ActionMenu.Overlay>
                      </ActionMenu>
                    ));
                  })()}
                </ActionList>
              </ScrollableActionListContainer>
            </ActionMenu.Overlay>
          </ActionMenu>
        </ItemsList>
      </ItemsContainer>
      <SectionWarning warning={warning} />
    </SectionContainer>
  );
}

const SearchContainer = styled.div`
  padding: var(--space__sm);
  border-bottom: 1px solid var(--borderColor-default);
`;

const ScrollableActionListContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
`;
